import { message } from 'antd';
import store from 'store';
import { AxiosError, AxiosResponse } from 'axios';
import {
  CREATE_FORM,
  FORM_AUDIENCE,
  FORM_AUDIENCE_ADD_NEW_LIST,
  DELETE_FORM,
  FORMS_LISTING,
  FORM_AUDIENCE_FILTER,
  FORM_DETAILS,
  FORM_SEARCH_AUDIENCE,
  UPDATE_FORM,
} from 'constants/types/formTypes';
import {
  createNewFormService,
  formAudienceCreateNewListService,
  getFormsService,
  deleteFormService,
  getFormAudienceService,
  getFormDetailsService,
  updateFormService,
} from 'modules/Services/SettingsService';
import { IFormCreateNewListResponse, IFormDetailsResponse } from '../Manage/interfaces';
import { IActionData, IFieldData, IFormAudienceParams, IUpdateAction } from './interfaces';
import { getDateObject, objectToQueryParams } from 'utils';
import { serializeForms } from './utils';
import { format } from 'date-fns';

const { dispatch } = store;

export const getFormDetailsAction = (formId: number) => {
  dispatch({
    type: FORM_DETAILS.LOAD,
  });
  getFormDetailsService(formId)
    .then((res: AxiosResponse<IFormDetailsResponse>) => {
      const { data, success, message } = res.data;
      if (success) {
        dispatch({
          type: FORM_DETAILS.SUCCESS,
          payload: {
            data,
          },
        });
      } else {
        dispatch({
          type: FORM_DETAILS.FAIL,
        });
      }
    })
    .catch((err: AxiosError<IFormDetailsResponse>) => {
      console.log('err', err);
      dispatch({
        type: FORM_DETAILS.FAIL,
      });
    });
};

export const getFormAudienceAction = (params: IFormAudienceParams) => {
  dispatch({
    type: params.searchTerm ? FORM_SEARCH_AUDIENCE.LOAD : FORM_AUDIENCE.LOAD,
  });
  getFormAudienceService(params)
    .then((res: AxiosResponse<IFormDetailsResponse>) => {
      if (res.data.success) {
        dispatch({
          type: params.searchTerm ? FORM_SEARCH_AUDIENCE.SUCCESS : FORM_AUDIENCE.SUCCESS,
          payload: {
            data: res?.data,
          },
        });
      } else {
        dispatch({
          type: params.searchTerm ? FORM_SEARCH_AUDIENCE.FAIL : FORM_AUDIENCE.FAIL,
        });
      }
    })
    .catch((err: AxiosError<IFormDetailsResponse>) => {
      console.log('err', err);
      dispatch({
        type: params.searchTerm ? FORM_SEARCH_AUDIENCE.FAIL : FORM_AUDIENCE.FAIL,
      });
    });
};

export const formAudienceSetSearchAction = (searchTerm: string) => {
  dispatch({
    type: FORM_SEARCH_AUDIENCE.SET_SEARCH_TERM,
    payload: {
      searchTerm,
    },
  });
};

export const formAudienceResetSearchAction = () => {
  dispatch({
    type: FORM_SEARCH_AUDIENCE.RESET_SEARCH_TERM,
  });
};

export const formAudienceResetContacts = () => {
  dispatch({
    type: FORM_AUDIENCE.RESET,
  });
};

export const formAudienceResetContactsListOnly = () => {
  dispatch({
    type: FORM_AUDIENCE.RESET_LIST_ONLY,
  });
};

export const formAudienceResetSearchContacts = () => {
  dispatch({
    type: FORM_SEARCH_AUDIENCE.RESET,
  });
};

export const formAudienceSetFilterCategorySelection = (categorySelected: any) => {
  const isCustom = categorySelected.value?.type === 'custom';
  const datesObject = getDateObject(categorySelected.value);

  const timeFilter = isCustom ? categorySelected.value?.value : datesObject;
  const time = isCustom
    ? {
        value: 'custom',
        label: format(new Date(timeFilter.start_date), 'MMM dd, yy') + ' to ' + format(new Date(timeFilter.end_date), 'MMM dd, yy'),
      }
    : categorySelected;

  dispatch({
    type: FORM_AUDIENCE_FILTER.SET_DATE_SELECTION,
    payload: time,
  });

  dispatch({
    type: FORM_AUDIENCE_FILTER.SET_DATE,
    payload: timeFilter,
  });
};

export const formAudienceResetFilter = () => {
  dispatch({
    type: FORM_AUDIENCE_FILTER.RESET,
  });
};

export const formAudienceCreateNewListAction = (params: IFormAudienceParams) => {
  dispatch({
    type: FORM_AUDIENCE_ADD_NEW_LIST.LOAD,
  });
  return new Promise((resolve, reject) => {
    formAudienceCreateNewListService(params)
      .then((res: AxiosResponse<IFormCreateNewListResponse>) => {
        if (res.data.success) {
          dispatch({
            type: FORM_AUDIENCE_ADD_NEW_LIST.SUCCESS,
            payload: {
              message: res?.data?.message,
            },
          });
          resolve({
            success: true,
            listId: res.data?.list_id,
          });
        } else {
          dispatch({
            type: FORM_AUDIENCE_ADD_NEW_LIST.FAIL,
            payload: {
              message: res?.data?.message,
            },
          });
          reject({ success: false });
        }
      })
      .catch((err: AxiosError<IFormDetailsResponse>) => {
        console.log('err', err);
        dispatch({
          type: FORM_AUDIENCE_ADD_NEW_LIST.FAIL,
          payload: {
            message: err,
          },
        });
        reject({ success: false });
      });
  });
};

export const createNewForm = (
  history: any,
  formName: string,
  formActions: IActionData[],
  formFields: IFieldData,
  formCampaignId?: string,
  event_type?: string,
) => {
  store.dispatch({ type: CREATE_FORM.LOAD });
  createNewFormService(formName, formActions, formFields, formCampaignId, event_type)
    .then((res: any) => {
      if (res.data.success) {
        const { id: formId } = res.data?.data;
        store.dispatch({
          type: CREATE_FORM.SUCCESS,
          payload: {
            message,
          },
        });
        // Toaster
        message.success({
          content: `Form handler has been created successfully`,
        });
        // Redirection to summary/detail page
        history.push(`/settings/forms?form_id=${formId}`);
      } else {
        store.dispatch({
          type: CREATE_FORM.FAIL,
        });
      }
    })
    .catch((err: any) => {
      console.log(err);
      store.dispatch({
        type: CREATE_FORM.FAIL,
      });
    });
};

export const updateForm = (
  history: any,
  formId: string,
  formName: string,
  formActions: IUpdateAction,
  formFields: IFieldData,
  formCampaignId?: string,
) => {
  store.dispatch({ type: UPDATE_FORM.LOAD });
  updateFormService(formId, formName, formActions, formFields, formCampaignId)
    .then((res: any) => {
      if (res.data.success) {
        store.dispatch({
          type: UPDATE_FORM.SUCCESS,
          payload: {
            message,
          },
        });
        // Redirection to summary/detail page
        history.push(`/settings/forms?form_id=${formId}`);
        // const formName = formName.length > 10 ? formName.splice(0, 20);
        // Toaster
        message.success({
          content: `Form handler has been updated successfully.`,
        });
      } else {
        store.dispatch({
          type: UPDATE_FORM.FAIL,
        });
      }
    })
    .catch((err: any) => {
      console.log(err);
      store.dispatch({
        type: UPDATE_FORM.FAIL,
      });
    });
};

export const deleteForm = (formIds: number[], formName?: string, selectAll?: boolean, excludedFormIds?: number[], searchTerm?: string) => {
  store.dispatch({ type: DELETE_FORM.LOAD });
  return new Promise((resolve, reject) => {
    deleteFormService(formIds, selectAll, excludedFormIds, searchTerm)
      .then((res: any) => {
        if (res.data.success) {
          store.dispatch({
            type: DELETE_FORM.SUCCESS,
            payload: {
              formIds,
              ...(selectAll && { selectAll }),
              ...(excludedFormIds && { excludedFormIds }),
            },
          });
          message.success({
            content: `${formName ? `Form handler  has` : 'Selected form handler(s) have'} been deleted successfully.`,
          });
          resolve({ success: true });
        } else {
          store.dispatch({
            type: DELETE_FORM.FAIL,
          });
        }
      })
      .catch((err: any) => {
        console.log(err);
        store.dispatch({
          type: DELETE_FORM.FAIL,
        });
        reject({ success: false });
      });
  });
};

export const getForms = (params: any) => {
  const queryParams = objectToQueryParams(params);
  store.dispatch({
    type: FORMS_LISTING.LOAD,
  });
  getFormsService(queryParams)
    .then((res: any) => {
      if (res.data.success) {
        const serializedData = serializeForms(res.data.data);
        store.dispatch({
          type: FORMS_LISTING.SUCCESS,
          payload: {
            message: res.data.message,
            searchTerm: params.search_term,
            forms: [...serializedData],
            totalForms: res.data.total_count,
          },
        });
      } else {
        store.dispatch({
          type: FORMS_LISTING.FAIL,
        });
      }
    })
    .catch((err: any) => {
      console.log(err);
      store.dispatch({
        type: FORMS_LISTING.FAIL,
      });
    });
};

export const resetForms = () => {
  store.dispatch({
    type: FORMS_LISTING.RESET,
  });
};
